import Axios from "axios";
import { limparProcessosDisponiveis } from "./processosDisponiveis";

export function parseJsonList(value, fallback = []) {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === "") return fallback;

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (error) {
    return fallback;
  }
}

export function normalizeTextList(value) {
  const fallback = Array.isArray(value) ? value : [];
  return parseJsonList(value, fallback)
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .filter((item, index, list) => list.indexOf(item) === index);
}

export function normalizePermission(value) {
  const normalized = String(value || "")
    .replace(/'/g, "")
    .replace(/"/g, "")
    .trim();
  if (!normalized) return "";
  return normalized.startsWith("ROLE_") ? normalized : `ROLE_${normalized}`;
}

export function normalizeTutorialProcess(value = {}) {
  const steps = Array.isArray(value.steps)
    ? value.steps
    : parseJsonList(value.stepsJson, []);

  return {
    ...value,
    title: value.title || "",
    subtitle: value.subtitle || "",
    slug: value.slug || "",
    category: value.category || "Tutorial",
    icon: value.icon || "mdi-lightbulb-on-outline",
    keywords: normalizeTextList(value.keywords),
    requiredPermissions: normalizeTextList(value.requiredPermissions).map(
      normalizePermission
    ),
    requiredRoutes: normalizeTextList(value.requiredRoutes),
    steps,
    stepsJson: value.stepsJson || JSON.stringify(steps, null, 2),
    active: value.active !== false,
    displayOrder: Number(value.displayOrder || 0),
  };
}

export function normalizeTutorialPayload(value = {}) {
  const steps = Array.isArray(value.steps)
    ? value.steps
    : parseJsonList(value.stepsJson, []);

  return {
    id: value.id,
    title: String(value.title || "").trim(),
    subtitle: String(value.subtitle || "").trim(),
    slug: String(value.slug || "").trim(),
    category: String(value.category || "").trim(),
    icon: String(value.icon || "").trim(),
    keywords: normalizeTextList(value.keywords),
    requiredPermissions: normalizeTextList(value.requiredPermissions).map(
      normalizePermission
    ),
    requiredRoutes: normalizeTextList(value.requiredRoutes),
    stepsJson:
      typeof value.stepsJson === "string"
        ? value.stepsJson
        : JSON.stringify(steps, null, 2),
    active: value.active !== false,
    displayOrder: Number(value.displayOrder || 0),
    // Tutorial em HTML: só vai quando muda (página nova ou "" para remover); ausente mantém o atual.
    ...(typeof value.staticHtml === "string"
      ? { staticHtml: value.staticHtml }
      : {}),
  };
}

// Gravações mudam o que os botões "Tutorial" das telas mostram.
function limparDepois(promessa) {
  return promessa.then((response) => {
    limparProcessosDisponiveis();
    return response;
  });
}

class TutorialDataService {
  available() {
    return Axios.get("tutorials/available");
  }

  getPage(params) {
    return Axios.get("tutorials/page", { params });
  }

  find(id) {
    return Axios.get(`tutorials/${id}`);
  }

  staticHtml(id) {
    return Axios.get(`tutorials/${id}/static`);
  }

  create(params) {
    return limparDepois(
      Axios.post("tutorials", normalizeTutorialPayload(params))
    );
  }

  update(params) {
    return limparDepois(
      Axios.put(`tutorials/${params.id}`, normalizeTutorialPayload(params))
    );
  }

  delete(id) {
    return limparDepois(Axios.delete(`tutorials/${id}`));
  }

  suggest(params) {
    return Axios.post("tutorials/suggest", params);
  }

  chat(params) {
    return Axios.post("tutorials/ai/chat", params);
  }

  trainAi(params = {}) {
    return Axios.post("tutorials/ai/train", params);
  }
}

export default new TutorialDataService();
